import type { LEER, Schluessel, Wert } from '../platform/einstellungen';
import { auswerten } from './geraeteEinstellungen';

/**
 * Was ein gescannter Einstellungscode ändern würde — in Relays Namen, egal woher er kommt.
 *
 * ⭐ **Relays Code ist der Hauptweg** (Entscheidung des Nutzers, 2026-10-08). Er trägt dieselben
 * Namen und Werte wie `SET_SETTINGS` und wird deshalb mit derselben Regel geprüft (`auswerten`):
 * `''` leert einen Text, ein ungültiger Wert fällt durch, ein unbekannter Name wird genannt.
 * Der Classic-Code wird in `classic/einstellungenQr.ts` in dieselbe Form übersetzt; ausserhalb
 * davon kommt kein Classic-Schlüssel vor.
 *
 * ⚠️ **Der Code gilt als ganzes Profil** (Entscheidung des Nutzers, 2026-10-08): er trägt alle
 * Einstellungen der Geräteart, gesetzt oder Vorgabe, und alles davon geht in die QR-Ablage —
 * über die Vorgaben der Geräteverwaltung.
 */
export interface QrVorschau {
  herkunft: 'relay' | 'classic';
  /** Relays Gruppenname, nur zur Anzeige. */
  gruppe: string | null;
  uebernommen: Partial<Record<Schluessel, Wert | typeof LEER | null>>;
  /** Für die Vorschau: alles, was nicht übernommen wird — unbekannt oder ungültig. */
  ignoriert: string[];
  /** Nur die Namen, die Stage nicht kennt — für Log und Warnung, nie mit Werten. */
  unbekannt: string[];
}

/**
 * Was ein QR-Code nie setzt: der Name gilt je Gerät, ein Code für eine ganze Gruppe (Vorgabe des
 * Nutzers, 2026-10-08). Relays Vorgabe ist leer — ohne diese Ausnahme löschte ein Gruppencode
 * den Namen jedes iPads, das ihn scannt. Setzen lässt er sich über MDM, die iPad-Seite,
 * `SET_SETTINGS` und die Einstellungen der App.
 */
const NUR_JE_GERAET: ReadonlySet<string> = new Set(['device_name']);

export function ausRelayEinstellungen(werte: Record<string, unknown>, gruppe: string | null): QrVorschau {
  const { setzen, unbekannt } = auswerten(werte);
  for (const name of NUR_JE_GERAET) delete setzen[name as keyof typeof setzen];
  const ignoriert = Object.keys(werte)
    .filter((name) => !(name in setzen))
    .sort();
  const uebernommen: QrVorschau['uebernommen'] = { ...setzen };
  // Ein Code von Relay meint ein Relay-Gerät — wie ein Classic-Code ein Classic-Gerät meint.
  if (Object.keys(uebernommen).length > 0 && uebernommen.server_protocol === undefined) {
    uebernommen.server_protocol = 'relay';
  }
  return { herkunft: 'relay', gruppe, uebernommen, ignoriert, unbekannt };
}
